// src/components/excel-upload/StatusBanner.tsx
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Upload,
    Loader2,
    PartyPopper,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { UploadStatus } from "./upload.types";

interface StatusBannerProps {
    status: UploadStatus;
    successCount: number;
    errorCount: number;
    totalCount: number;
    onDismiss?: () => void;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({
    status,
    successCount,
    errorCount,
    totalCount,
    onDismiss,
}) => {
    if (status === "idle") return null;

    const configs: any = {
        uploading: {
            bg: "from-blue-500 to-indigo-500",
            icon: <Loader2 className="h-5 w-5 animate-spin" />,
            title: "Uploading files...",
            subtitle: `Please wait while we process your files`,
        },
        completed: {
            bg: "from-emerald-500 to-green-500",
            icon: <PartyPopper className="h-5 w-5" />,
            title: "All uploads complete!",
            subtitle: `${successCount} file${successCount !== 1 ? "s" : ""} uploaded successfully`,
        },
        partial: {
            bg: "from-amber-500 to-orange-500",
            icon: <AlertTriangle className="h-5 w-5" />,
            title: "Upload partially complete",
            subtitle: `${successCount} succeeded, ${errorCount} failed`,
        },
        error: {
            bg: "from-red-500 to-rose-500",
            icon: <XCircle className="h-5 w-5" />,
            title: "Upload failed",
            subtitle: `${errorCount} file${errorCount !== 1 ? "s" : ""} failed to upload`,
        },
    };

    const config = configs[status];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className={cn(
                    "relative overflow-hidden rounded-2xl p-4 text-white",
                    `bg-gradient-to-r ${config.bg}`
                )}
            >
                {/* Background decoration */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                </div>

                <div className="relative flex items-center gap-4">
                    <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                        {config.icon}
                    </div>

                    <div className="flex-1">
                        <h4 className="font-semibold">{config.title}</h4>
                        <p className="text-sm text-white/80">{config.subtitle}</p>
                    </div>

                    {status !== "uploading" && onDismiss && (
                        <button
                            onClick={onDismiss}
                            className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                        >
                            <XCircle className="h-5 w-5" />
                        </button>
                    )}
                </div>

                {/* Progress indicator for uploading */}
                {status === "uploading" && (
                    <div className="relative mt-3">
                        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-white/60 rounded-full"
                                initial={{ width: "0%" }}
                                animate={{ width: "100%" }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            />
                        </div>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
};